import React from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SearchField } from "../shared";
import {
  NO_SUBCIRCLES_FILTER,
  type AliasFilter,
} from "../../hooks/useUserListFilterState";

interface SubcircleOption {
  circleId: string;
  name: string;
}

interface UsersFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  showSubcircleFilters: boolean;
  selectedSubcircleId: string;
  selectedSubcircleName: string;
  subcircleMenuAnchor: HTMLElement | null;
  onSubcircleMenuOpen: (anchor: HTMLElement) => void;
  onSubcircleMenuClose: () => void;
  onSubcircleSelect: (subcircleId: string) => void;
  subcircles: SubcircleOption[];
  isNoSubcirclesFilter: boolean;
  aliasFilter: AliasFilter;
  aliasFilterMenuAnchor: HTMLElement | null;
  onAliasFilterMenuOpen: (anchor: HTMLElement) => void;
  onAliasFilterMenuClose: () => void;
  onAliasFilterSelect: (filter: AliasFilter) => void;
  hasUserListFilters: boolean;
  onClearFilters: () => void;
  showUserResultCount: boolean;
  userResultCount: number;
  isUserListLoading: boolean;
  isAliasDataLoading: boolean;
  showInvitationResultCount: boolean;
  invitationResultCount: number;
  isLoadingInvitations: boolean;
  showJoinRequestResultCount: boolean;
  joinRequestResultCount: number;
  isLoadingJoinRequests: boolean;
  activeTab: number;
}

export const UsersFilterBar: React.FC<UsersFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  showSubcircleFilters,
  selectedSubcircleId,
  selectedSubcircleName,
  subcircleMenuAnchor,
  onSubcircleMenuOpen,
  onSubcircleMenuClose,
  onSubcircleSelect,
  subcircles,
  isNoSubcirclesFilter,
  aliasFilter,
  aliasFilterMenuAnchor,
  onAliasFilterMenuOpen,
  onAliasFilterMenuClose,
  onAliasFilterSelect,
  hasUserListFilters,
  onClearFilters,
  showUserResultCount,
  userResultCount,
  isUserListLoading,
  isAliasDataLoading,
  showInvitationResultCount,
  invitationResultCount,
  isLoadingInvitations,
  showJoinRequestResultCount,
  joinRequestResultCount,
  isLoadingJoinRequests,
  activeTab,
}) => {
  const theme = useTheme();

  const filterButtonSx = (active: boolean) => ({
    textTransform: "none" as const,
    fontWeight: 600,
    borderRadius: 1,
    boxShadow: "none",
    minWidth: { xs: "100%", sm: active ? 180 : 160 },
    px: 2,
    py: 0.75,
    height: 36,
    alignSelf: { xs: "stretch" as const, sm: "flex-start" as const },
    backgroundColor: active ? theme.palette.primary.main : theme.palette.common.white,
    color: active ? theme.palette.common.white : theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: active
        ? theme.palette.primary.dark
        : theme.palette.primary.light + "10",
      borderColor: theme.palette.primary.dark,
    },
  });

  return (
    <>
      <SearchField
        value={searchQuery}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        sx={{ width: "100%" }}
      />

      {showSubcircleFilters ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mt: 1.5, flexWrap: "wrap" }}
        >
          <Button
            variant="contained"
            onClick={(e) => onSubcircleMenuOpen(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={filterButtonSx(Boolean(selectedSubcircleId))}
          >
            {selectedSubcircleName}
          </Button>
          <Button
            variant="contained"
            onClick={(e) => onAliasFilterMenuOpen(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={filterButtonSx(aliasFilter !== "all")}
          >
            {aliasFilter === "all"
              ? "All aliases"
              : aliasFilter === "with"
                ? "With alias"
                : "Without alias"}
          </Button>
        </Stack>
      ) : null}

      {hasUserListFilters ? (
        <Box sx={{ mt: 1.5 }}>
          <Chip
            label="Clear filters"
            variant="outlined"
            size="small"
            onClick={onClearFilters}
            sx={{ cursor: "pointer" }}
          />
        </Box>
      ) : null}

      {activeTab === 0 &&
        hasUserListFilters &&
        !isUserListLoading &&
        !(aliasFilter !== "all" && isAliasDataLoading) &&
        showUserResultCount ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {userResultCount} user{userResultCount !== 1 ? "s" : ""} found
        </Typography>
      ) : null}

      {activeTab === 1 && showInvitationResultCount && !isLoadingInvitations ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {invitationResultCount} invitation
          {invitationResultCount !== 1 ? "s" : ""} found
        </Typography>
      ) : null}

      {activeTab === 2 && showJoinRequestResultCount && !isLoadingJoinRequests ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {joinRequestResultCount} request
          {joinRequestResultCount !== 1 ? "s" : ""} found
        </Typography>
      ) : null}

      <Menu
        anchorEl={subcircleMenuAnchor}
        open={Boolean(subcircleMenuAnchor)}
        onClose={onSubcircleMenuClose}
      >
        <MenuItem selected={!selectedSubcircleId} onClick={() => onSubcircleSelect("")}>
          All subcircles
        </MenuItem>
        <MenuItem
          selected={isNoSubcirclesFilter}
          onClick={() => onSubcircleSelect(NO_SUBCIRCLES_FILTER)}
        >
          No subcircles
        </MenuItem>
        {subcircles.map((circle) => (
          <MenuItem
            key={circle.circleId}
            selected={selectedSubcircleId === circle.circleId}
            onClick={() => onSubcircleSelect(circle.circleId)}
          >
            {circle.name}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={aliasFilterMenuAnchor}
        open={Boolean(aliasFilterMenuAnchor)}
        onClose={onAliasFilterMenuClose}
      >
        <MenuItem
          selected={aliasFilter === "all"}
          onClick={() => {
            onAliasFilterSelect("all");
            onAliasFilterMenuClose();
          }}
        >
          All aliases
        </MenuItem>
        <MenuItem
          selected={aliasFilter === "with"}
          onClick={() => {
            onAliasFilterSelect("with");
            onAliasFilterMenuClose();
          }}
        >
          With alias
        </MenuItem>
        <MenuItem
          selected={aliasFilter === "without"}
          onClick={() => {
            onAliasFilterSelect("without");
            onAliasFilterMenuClose();
          }}
        >
          Without alias
        </MenuItem>
      </Menu>
    </>
  );
};

export default UsersFilterBar;
